import {DataModificationsHeader} from 'audit/DataModifications';
import {pluralize, redirectForDownload} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Tag} from 'folder-items/FileConstants';
import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import AppDispatcher from 'lib/Dispatcher';
import {ProcessOutputSubmitParams} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import notifications from 'notifications';
import {getTagUpdateParams} from 'schemas/tags/TagUtil';
import {isServerRequestError} from 'util/Net';
import uniqueId from 'util/uniqueId';

import {ProcessTaskGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import * as ProcessManagementDAO from './dao/ProcessManagementDAO';
import {asyncBulkUpdateProcessWithDataMods} from './dashboard/bulk-edit/ProcessTaskEditorDropdown';
import {SidepanelProcessItem} from './dashboard/ProcessManagementSidepanelConstants';
import {PROCESS_MANAGEMENT_ITEM_TYPE_TO_READABLE, ProcessManagementItemType} from './ProcessConstants';
import submitOutputsWithConfirmation, {
  isUpdateServerProcessOutput,
} from './tables/submitOutputsWithConfirmation';

export async function exportToCSV(
  selectedType: ProcessManagementItemType,
  ids: Array<string>
): Promise<void> {
  const notificationId = uniqueId('export-process-management-to-csv-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  try {
    const response = await ProcessManagementDAO.exportToCSV(selectedType, ids);
    redirectForDownload(response.url);
    notifications.success('Export complete. Downloading...', {id: notificationId});
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
    }
  }
}

export async function updateProcessOutputTagsWithConfirmation(
  updateOutputs: Array<ProcessManagementDAO.UpdateServerProcessOutput>
): Promise<void> {
  await submitOutputsWithConfirmation({
    outputs: updateOutputs,
    onRequestSubmit: async (outputSubmitParams: ProcessOutputSubmitParams): Promise<void> => {
      try {
        const itemIds = updateOutputs
          .filter((output) => isUpdateServerProcessOutput(output))
          .map((item) => item.id);
        await asyncBulkUpdateProcessWithDataMods(
          itemIds,
          async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
            await ProcessManagementDAO.bulkCreateOrUpdateProcessOutputs({
              processOutputs: outputSubmitParams.processOutputs,
              dataModsHeader,
            });
          },
          ActionOrigin.WORKFLOWS_DASHBOARD
        );
        notifications.success(
          `Successfully updated ${pluralize(
            updateOutputs.length,
            PROCESS_MANAGEMENT_ITEM_TYPE_TO_READABLE[ModelTypeConstants.PROCESS_OUTPUT]
          )}`
        );
        AppDispatcher.dispatch({
          actionType: EventConstants.PROCESS_MANAGEMENT_ITEMS_UPDATED,
          itemIds: updateOutputs.map(({id}) => id),
        });
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        if (isServerRequestError(e)) {
          notifications.notifyError(e);
        }
      }
    },
  });
}

export async function updateProcessOutputTags(
  selectedItems: Array<SidepanelProcessItem>,
  newTag: Tag,
  sourceType: string,
  actionOrigin: ActionOrigin,
  actionMethod: ActionMethod
): Promise<void> {
  const fieldUpdate = selectedItems.map((item) => ({
    id: item.id,
    fields: {[newTag.name]: getTagUpdateParams(newTag)},
    sourceType,
    actionOrigin,
    actionMethod,
  }));
  await updateProcessOutputTagsWithConfirmation(fieldUpdate);
}

export async function copyTasks(taskIds: Array<string>): Promise<Array<ProcessTaskGraphQL> | null> {
  let tasks: Array<ProcessTaskGraphQL> | null = null;
  try {
    tasks = (await ProcessManagementDAO.bulkCopyTasks(taskIds)).tasks;
    notifications.success(`Successfully copied ${taskIds.length} ${pluralize(taskIds.length, 'task')}`);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
    }
  }
  return tasks;
}

export function invalidateInDashboard(items: Array<{id: string}>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.PROCESS_MANAGEMENT_ITEMS_UPDATED,
    itemIds: items.map(({id}) => id),
  });
}
